import { Resource } from "@app/resources";
import { TimeDistanceCalculatorType } from "@app/projects/enumerators";

export default interface TemplateCollectionMetadata {
  templateCollectionId: number;
  resource: Resource;
  templates?: TemplateMetadata[];
  correlationIds?: string[];
}

export interface TemplateMetadata {
  accessCriteriaSpecificationGranularity: AccessCriteriaGranularityEnum;
  attestationCrosswalkCodeMapSetId?: number;
  servicingAnalysisGranularity: ServicingAnalysisGranularityEnum;
  specialtyDefinitionId?: number;
  templateCollectionId: number;
  templateId: string;
  templateName: string;
  templateDescription: string;
  createdDate: string;
  closestProviderCount: number;
  correlationId: string;
  memberSetId?: number;
  zipClassType: string;
  accessCodeMapSetId?: number;
  useMedicareRatebook: boolean;
  timeDistanceCalculatorTypes: TimeDistanceCalculatorType[];
  motiveCrosswalkCodeMapSetId?: number;
  regionalCodeMapSetId?: number;
  usesPlanningRegion: boolean;
}

export interface CCIIOTemplateMetaData {
  templateCollectionId: number;
  correlationId: string;
  templateId: string;
  resourceName: string;
}

export interface TemplateSpecialtyGroup {
  code: string;
  displayOrder: number;
  hasCMSApplicationCreditPercentages?: boolean;
  hasTelehealthAccessPercentageCredits?: boolean;
  name: string;
  servicingCountBy: ServicingCountTypeEnum;
  servicingMinimumType: ServicingMinimumTypeEnum;
}

export interface TemplateCategory {
  displayOrder: number;
  displayName: string;
  specialtyGroupCodes: string[];
}

export interface TemplateAccessServicingCriteria extends TemplateCriteriaBase {
  accessMaximumDistanceAllowed: number;
  accessMaximumTimeAllowed: number;
  accessMinimumProviders: number;
  accessPassPercentage: number;
  calculationMethod: CalculationMethodEnum;
  servicingMinimumCount: number;
  servicingRatio: string;
}

export interface TemplateAccessCriteria extends TemplateCriteriaBase {
  maximumDistanceAllowed: number;
  maximumTimeAllowed: number;
  minimumProviderCount: number;
  passPercentage: number;
  calculationMethod: CalculationMethodEnum;
  conAccessCreditPercentage: number;
  isAccessCustomization: boolean;
  telehealthAccessCreditPercentage: number;
  cmsApplicationCreditPercentage: number;
}

export interface TemplateServicingCriteria extends TemplateCriteriaBase {
  servicingAnalysisGranularity: ServicingAnalysisGranularityEnum;
  servicingMinimumCount: number;
  servicingRatio: string;
}

interface TemplateCriteriaBase {
  geographyId: string;
  geographyDescription: string;
  countyName: string;
  stateName: string;
}

export enum CalculationMethodEnum {
  Unknown = "unknown",

  /*
   * Calculate distance only
   */
  Distance = "distance",

  /*
   * Calculate time only
   */
  Time = "time",

  /*
   * Calculate both time and distance simultaneously
   */
  TimeAndDistance = "timeAndDistance",

  /*
   * Calculate both time and distance simultaneously
   */
  TimeOrDistance = "timeOrDistance",
}

export enum AccessCriteriaGranularityEnum {
  Unknown = "Unknown",
  /**
   * A percentage of members must have access to some number of providers by time, distance, time and distance, or time or distance,
   * without respect to geography.
   */
  Standard = "Standard",
  /**
   * Same as Standard, but with respect to the member's zip code classification.
   */
  ZipClass = "ZipClass",
  /**
   * Same as Standard, but with respect to the member's county classification.
   */
  CountyClass = "CountyClass",
  /**
   * Same as Standard, but with respect to the member's zip code.
   */
  ZipCode = "ZipCode",
  /**
   * Same as Standard, but with respect to the member's county.
   */
  CountySSA = "CountySSA",
}

export enum ServicingAnalysisGranularityEnum {
  Unknown = "unknown",

  /*
   * Indicates servicing analysis criteria are specified at the county level.
   */
  County = "county",

  /*
   * Indicates servicing analysis criteria are specified at the state level.
   */
  State = "state",

  /*
   * Indicates servicing analysis criteria are specified at the region level, which encompasses a homogeneous set of one or more zip codes, counties, or states.
   */
  Region = "region",
}

export enum ServicingMinimumTypeEnum {
  Unknown = "unknown",

  /*
   * Determine servicing by minimum provider count.
   */
  MinimumProviders = "minimumProviders",

  /*
   * Determine servicing by ratio of providers to members.
   */
  Ratio = "ratio",
}

export enum ServicingCountTypeEnum {
  Unknown = "unknown",

  Providers = "providers",

  Beds = "beds",

  Locations = "locations",
}

export enum ServicingAnalysisTypeEnum {
  Unknown = "unknown",
  InRegionOrCoversMember = "inRegionOrCoversMember",
  InRegion = "inRegion",
  MinimizeOutOfRegion = "minimizeOutOfRegion",
  InCountyOrAdjacent = "inCountyOrAdjacent"
}

export enum ProviderRegionTypeEnum {
  Unknown = "unknown",
  InRegionOrCoversMember = "inRegionOrCoversMember",
  InRegion = "inRegion",
  InCountyOrAdjacent = "inCountyOrAdjacent"
}